import { useNavigate, useSearch } from "@tanstack/react-router";
import { Dropper, Folder, Ruler, Settings01, Tag01, ZapFast } from "@untitledui/icons";
import { useState } from "react";
import { useCohortRulesCount } from "@/api/cohort-rules";
import { useCreatorCohorts } from "@/api/creator-cohorts";
import { useDismissHero } from "@/api/user-preferences";
import { PageHero, PageHeroKey } from "@/components/application/page-hero/page-hero";
import { Button } from "@/components/base/buttons/button";
import { CohortRulesDrawer } from "@/components/creator-cohorts/cohort-rules-drawer";
import { LoadingPageState } from "@/components/loading-page-state";
import { PageLayout } from "@/components/page-layout";
import type { creatorCohortsRoute } from "@/router";
import { CohortLibraryModal } from "./cohort-library-modal";
import { CohortList } from "./cohort-list";
import { CohortsEmptyState } from "./cohorts-empty-state";

export function CreatorCohortsPage() {
  const [rulesPanelOpen, setRulesPanelOpen] = useState(false);
  const [inspectedRuleId, setInspectedRuleId] = useState<string>();
  const navigate = useNavigate();
  const { manageCohorts } = useSearch({ strict: false }) as ReturnType<(typeof creatorCohortsRoute)["useSearch"]>;
  const [libraryOpen, setLibraryOpen] = useState(manageCohorts === true);
  const { data: rulesCount } = useCohortRulesCount();
  const { data: serverCohorts, isLoading } = useCreatorCohorts();
  const dismissHero = useDismissHero();

  const handleLibraryOpenChange = (open: boolean) => {
    setLibraryOpen(open);

    if (!open && manageCohorts) {
      navigate({ to: ".", search: {}, replace: true });
    }
  };

  const openRules = (ruleId?: string) => {
    setInspectedRuleId(ruleId);
    setRulesPanelOpen(true);
  };

  if (isLoading) return <LoadingPageState />;

  if ((serverCohorts?.length ?? 0) === 0) {
    return (
      <>
        <CohortsEmptyState onCreate={() => setLibraryOpen(true)} />
        <CohortLibraryModal
          open={libraryOpen}
          onOpenChange={handleLibraryOpenChange}
          startInNewCohort
          onSaved={() => dismissHero.mutate(PageHeroKey.Cohorts)}
        />
      </>
    );
  }

  return (
    <PageLayout
      title="Cohorts"
      icon={Tag01}
      subtitle="Organize your creators with custom cohorts to segment, filter, and reach the right people faster."
      callToAction={
        <div className="flex items-center gap-3">
          <Button color="secondary" iconLeading={Ruler} onClick={() => openRules()}>
            See Rules{rulesCount ? ` (${rulesCount.total})` : ""}
          </Button>
          <Button color="primary" iconLeading={Settings01} onClick={() => setLibraryOpen(true)}>
            Manage All Cohorts
          </Button>
        </div>
      }
    >
      <PageHero
        pageKey={PageHeroKey.Cohorts}
        title="Group creators the way you work."
        text1={{ icon: Folder, message: "Group creators manually, from a CSV or with a rule inside a cohort." }}
        text2={{ icon: ZapFast, message: "Define it by GMV, followers, engagement or your own rule." }}
        text3={{ icon: Dropper, message: "Color code cohorts for fast identification and reorder by importance." }}
        image="/heroes/cohorts.png"
      />
      <CohortList onInspectRule={openRules} />

      <CohortLibraryModal open={libraryOpen} onOpenChange={handleLibraryOpenChange} />

      <CohortRulesDrawer open={rulesPanelOpen} onOpenChange={setRulesPanelOpen} initialRuleId={inspectedRuleId} />
    </PageLayout>
  );
}
